/**
 * Cloud Operations — Deploy Configure tab (contract-driven).
 *
 * A deploy is authorized by an entitled deployment line on the customer's signed
 * order form, so the operator first picks a Customer and an Entitled deployment.
 * The selected line's envelope fixes the infra — deployment mode, cloud provider,
 * region, instance size and SLA tier — which is shown read-only for confirmation;
 * the operator supplies only the deployment name, platform version, resilience
 * posture and (for a self-hosted contract) the cloud-account credentials.
 *
 *   top:   Customer + Order-Form deployment picker (+ already-provisioned list)
 *   left:  Contracted deployment spec (read-only) + self-hosted creds when contracted
 *   right: Resilience & Scaling + Deployment name + Platform version + Deploy CTA
 *
 * Submitting calls quickDeploy() with the customer + order-line ids. The backend
 * fulfils the line through the shared OrderLineFulfillmentService — provisioning the
 * Instance and entitlements, then bringing up the cluster with the contracted infra —
 * and hard-rejects any line that is not an entitled, un-provisioned deployment.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, btnPrimary, inp, Field, Banner, modeLabel } from './_ui.jsx';
import { EntityPicker, CustomerPicker } from '../../components/v2';
import { quickDeploy } from '../../services/cloudOperationsApi';

const SIZES = [
  { id: 'XS', label: 'XS · 2 vCPU · 4 GB — Sandbox',  cpu: 2,  ram: 4  },
  { id: 'S',  label: 'S · 2 vCPU · 8 GB — Dev/Test',  cpu: 2,  ram: 8  },
  { id: 'M',  label: 'M · 4 vCPU · 8 GB — Standard',  cpu: 4,  ram: 8  },
  { id: 'LG', label: 'LG · 8 vCPU · 16 GB — Prod',    cpu: 8,  ram: 16 },
  { id: 'XL', label: 'XL · 16 vCPU · 32 GB — Prod+',  cpu: 16, ram: 32 },
];

const SLA_TIERS = [
  { id: 'single_region',     label: 'Single-region',                       hint: '99.9% · one region · cheapest' },
  { id: 'multi_region_ap',   label: 'Multi-region (active-passive)',       hint: '99.95% · failover · 2× regions' },
  { id: 'multi_region_aa',   label: 'Multi-region (active-active)',        hint: '99.99% · always-on · 2× regions' },
];

// NOTE: the deployable version list is NOT hard-coded here any more. It is
// served by GET /api/v1/cloud-operations/platform-versions (PlatformVersionCatalog)
// and arrives as the `platformVersions` prop. The previous hard-coded copy drifted
// behind the release train — it still called 1.0.0-rc1 "latest" after rc2 shipped.

const PROVIDERS = [
  { id: 'gcp',   label: 'GCP',   tag: 'G', accent: '#1f5fa6' },
  { id: 'aws',   label: 'AWS',   tag: 'A', accent: '#c58b00' },
  { id: 'azure', label: 'AZURE', tag: 'Z', accent: '#1f5fa6' },
];

export default function DeployConfigureTab({
  form, updateRoot, updateCloud, updateResil,
  platformVersions = [], preflight = null,
  customers = [], customersLoading = false,
  entitled = [], provisioned = [], deploymentsLoading = false,
  selectedEnvelope = null,
  onSelectCustomer, onSelectLine,
}) {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  // Contract-driven: a deploy is authorized by an entitled order-form line. Its
  // envelope fixes the infra (mode, provider, region, size, SLA); the operator
  // only supplies the name, platform version and (self-hosted) cloud credentials.
  const env = selectedEnvelope;
  const hasCustomer = !!form.customer_id;
  const hasLine = !!env;
  const contractedMode = hasLine ? env.deploymentMode : form.mode;
  const isSelfHosted = contractedMode === 'self_hosted';
  const effProvider = hasLine ? env.provider : form.provider;
  const providerLabel = PROVIDERS.find((p) => p.id === effProvider)?.label
    || String(effProvider || '').toUpperCase();
  const ctaLabel = isSelfHosted
    ? `Deploy to ${providerLabel}`
    : 'Deploy to Prove7 Cloud';

  // Validate just enough to prevent obvious 400s. Backend revalidates and
  // hard-rejects a line that is not an entitled, un-provisioned deployment.
  const errors = useMemo(() => validate(form, env), [form, env]);
  const canSubmit = errors.length === 0 && !submitting;

  const onDeploy = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload(form, env);
      const res = await quickDeploy(payload);
      const id = res?.deployment?.id;
      if (id) nav(`/cloud-operations/advanced/deployments/${id}`);
      else    nav('/cloud-operations?tab=existing');
    } catch (e) {
      setError(e?.message || String(e));
      setSubmitting(false);
    }
  };

  return (
    <div>
      {error && <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>}

      {/* Customer + Order Form — the signed contract that authorizes this deploy. */}
      <OrderFormPicker
        customers={customers}
        customersLoading={customersLoading}
        customerId={form.customer_id}
        onSelectCustomer={onSelectCustomer}
        entitled={entitled}
        provisioned={provisioned}
        deploymentsLoading={deploymentsLoading}
        orderLineId={form.order_line_id}
        onSelectLine={onSelectLine}
        hasCustomer={hasCustomer}
      />

      {!hasLine ? (
        <Guidance>
          {!hasCustomer
            ? 'Choose a customer to see the deployments their signed order forms entitle them to.'
            : deploymentsLoading
              ? 'Loading this customer’s order-form deployments…'
              : entitled.length === 0
                ? 'This customer has no un-provisioned deployments on their active order forms. Add a deployment line to an order form, then return here to provision it.'
                : 'Select an entitled deployment above to review its contracted specification and provision it.'}
        </Guidance>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}>
          {/* LEFT — the contracted deployment spec (read-only) + self-hosted creds when
              the order form contracts a self-hosted deployment. Infra is fixed by the
              order form, so it is shown for confirmation rather than edited here. */}
          <Panel
            icon="☁"
            title="Contracted deployment"
            subtitle="Fixed by the signed order form"
          >
            <ContractedSpec env={env} />

            {isSelfHosted && (
              <SelfHostedCreds
                cloud={form.cloud_account}
                provider={env.provider}
                update={updateCloud}
              />
            )}
          </Panel>

          {/* RIGHT — Resilience + name + version + CTA */}
          <Panel
            icon="⊘"
            title="Resilience & Scaling"
            accentBg="#eef4ff"
          >
            <ResilienceToggles value={form.resilience} onChange={updateResil} />

            <div style={{ height: 1, background: T.stroke2, margin: '16px 0' }} />

            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Deployment Name" required hint="Identifies this deployment in the catalog and runs.">
                <input
                  style={inp}
                  placeholder="prod-us-central"
                  value={form.deployment_name}
                  onChange={(e) => updateRoot({ deployment_name: e.target.value })}
                />
              </Field>

              <Field label="Platform Version">
                {/* Searchable + paginated combobox (EntityPicker) — the version list is the live
                    published-release feed and can run long, so this replaces the OS-drawn <select>.
                    _ord preserves the server's newest-first order (EntityPicker sorts ascending). */}
                <EntityPicker
                  ariaLabel="Platform version"
                  items={platformVersions.map((v, i) => ({ ...v, _ord: i }))}
                  value={form.platform_version}
                  onChange={(v) => updateRoot({ platform_version: v || '' })}
                  getId={(v) => v.version}
                  getLabel={(v) => v.version}
                  getBadge={(v) => (v.latest ? 'latest' : null)}
                  getSearchText={(v) => v.version}
                  getSortKey={(v) => String(v._ord ?? 0).padStart(5, '0')}
                  loading={platformVersions.length === 0}
                  loadingText="Loading versions…"
                  placeholder="Choose a version"
                  searchPlaceholder="Search versions (e.g. rc25)"
                  emptyMessage={(q) => (q
                    ? `No version matches “${q}”.`
                    : 'No deployable versions — add a platform GitHub connector or set an explicit list.')}
                />
              </Field>
            </div>

            <PreflightBanner preflight={preflight} />

            {errors.length > 0 && (
              <div style={{
                marginTop: 12, padding: '8px 12px',
                background: T.warnBg, color: T.warn,
                border: `1px solid ${T.warn}33`, borderRadius: 4,
                fontSize: 12,
              }}>
                <strong>Required to deploy:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {errors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={onDeploy}
              disabled={!canSubmit}
              style={{
                ...btnPrimary,
                marginTop: 16,
                padding: '11px 18px',
                fontSize: 14,
                width: '100%',
                justifyContent: 'center',
                opacity: canSubmit ? 1 : 0.55,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Deploying…' : ctaLabel}
            </button>

            <div style={{ marginTop: 8, fontSize: 11, color: T.text3, textAlign: 'center' }}>
              Mode: {modeLabel(contractedMode)} · {SIZES.find(s => s.id === env.instanceSize)?.label || env.instanceSize}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

/**
 * Customer + entitled-deployment chooser. The deploy is authorized by a line on a
 * signed order form (cumulative across every active order form for the customer),
 * so this is the first thing the operator picks. Already-provisioned deployments
 * are shown read-only so it is obvious what the customer already has.
 */
function OrderFormPicker({
  customers, customersLoading, customerId, onSelectCustomer,
  entitled, provisioned, deploymentsLoading, orderLineId, onSelectLine, hasCustomer,
}) {
  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.stroke}`,
      borderRadius: 10,
      padding: 18,
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 4 }}>
        Customer &amp; Order Form
      </div>
      <div style={{ fontSize: 12, color: T.text2, marginBottom: 14 }}>
        Provision a deployment the customer’s signed order form entitles them to. Infra is taken
        from the order form — Prove7 deploys exactly what was contracted.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <Field label="Customer" required>
          <CustomerPicker
            customers={customers}
            value={customerId}
            onChange={onSelectCustomer}
            loading={customersLoading}
            ariaLabel="Select a customer to deploy for"
          />
        </Field>

        <Field label="Entitled deployment" required
          hint={hasCustomer ? 'Un-provisioned deployment lines from active order forms.' : 'Choose a customer first.'}>
          <EntityPicker
            ariaLabel="Entitled order-form deployment"
            items={entitled}
            value={orderLineId}
            onChange={onSelectLine}
            getId={(e) => e.lineId}
            getLabel={entitledLabel}
            getSearchText={(e) => entitledLabel(e)}
            getBadge={(e) => e.editionKey || null}
            disabled={!hasCustomer}
            loading={deploymentsLoading}
            loadingText="Loading deployments…"
            placeholder={hasCustomer ? 'Choose a deployment' : 'Choose a customer first'}
            searchPlaceholder="Search by order form or region"
            emptyMessage={() => (hasCustomer
              ? 'No un-provisioned deployments on this customer’s active order forms.'
              : 'Choose a customer first.')}
          />
        </Field>
      </div>

      {hasCustomer && provisioned && provisioned.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
            Already provisioned
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {provisioned.map((e) => (
              <span key={e.lineId} style={{
                fontSize: 11, color: T.text2,
                background: T.bgAlt, border: `1px solid ${T.stroke2}`,
                borderRadius: 999, padding: '3px 10px',
              }}>
                {entitledLabel(e)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function entitledLabel(e) {
  if (!e) return null;
  const order = e.orderNumber || (e.orderId ? `order ${String(e.orderId).slice(0, 8)}` : 'order');
  const line = e.lineNo != null ? `#${e.lineNo}` : '';
  const edition = e.editionKey || '—';
  const where = [e.provider, e.region].filter(Boolean).join('/');
  const size = e.instanceSize ? ` · ${e.instanceSize}` : '';
  return `${order} ${line} · ${edition}${where ? ` · ${where}` : ''}${size}`.replace(/\s+/g, ' ').trim();
}

/** Read-only presentation of the infra a selected order-form line contracts. */
function ContractedSpec({ env }) {
  const sizeLabel = SIZES.find((s) => s.id === env.instanceSize)?.label || env.instanceSize;
  const slaLabel = SLA_TIERS.find((t) => t.id === env.slaTier)?.label || env.slaTier;
  const modules = Array.isArray(env.modules) ? env.modules : [];
  const rows = [
    ['Order form', env.orderNumber || (env.orderId ? String(env.orderId).slice(0, 8) : '—')],
    ['Edition', env.editionKey || '—'],
    ['Deployment mode', modeLabel(env.deploymentMode)],
    ['Cloud provider', String(env.provider || '').toUpperCase() || '—'],
    ['Region', env.region || '—'],
    ['Paired region', env.pairedRegion || '—'],
    ['Instance size', sizeLabel || '—'],
    ['SLA tier', slaLabel || '—'],
    ['Data residency', env.dataResidency || '—'],
  ];
  return (
    <div style={{ display: 'grid', gap: 0 }}>
      {rows.map(([k, v], i) => (
        <div key={k} style={{
          display: 'flex', justifyContent: 'space-between', gap: 12,
          padding: '8px 0',
          borderTop: i === 0 ? 'none' : `1px solid ${T.stroke2}`,
        }}>
          <span style={{ fontSize: 12, color: T.text2 }}>{k}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, textAlign: 'right' }}>{v}</span>
        </div>
      ))}
      {modules.length > 0 && (
        <div style={{ paddingTop: 10, borderTop: `1px solid ${T.stroke2}`, marginTop: 2 }}>
          <div style={{ fontSize: 12, color: T.text2, marginBottom: 6 }}>Modules</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {modules.map((m) => (
              <span key={m} style={{
                fontSize: 11, fontWeight: 600, color: T.action,
                background: `${T.action}14`, border: `1px solid ${T.action}33`,
                borderRadius: 999, padding: '3px 10px',
              }}>{m}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Guidance({ children }) {
  return (
    <div style={{
      background: T.bgCard,
      border: `1px dashed ${T.stroke}`,
      borderRadius: 10,
      padding: '28px 20px',
      textAlign: 'center',
      color: T.text2,
      fontSize: 13,
    }}>
      {children}
    </div>
  );
}

function Panel({ icon, title, subtitle, accentBg = T.bgAlt, children }) {
  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.stroke}`,
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        background: accentBg,
        padding: '14px 18px',
        borderBottom: `1px solid ${T.stroke2}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{
          width: 24, height: 24, borderRadius: 4,
          display: 'grid', placeItems: 'center',
          background: 'transparent', color: T.ink, fontSize: 14, fontWeight: 700,
        }}>{icon}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}

function ResilienceToggles({ value, onChange }) {
  const rows = [
    { key: 'multi_az',         label: 'Multi-AZ Deployment',     hint: 'Distribute across availability zones' },
    { key: 'auto_scaling',     label: 'Auto-scaling',            hint: 'Scale workers based on queue depth' },
    { key: 'tls_termination',  label: 'TLS Termination',         hint: 'Managed certs via ACM / Let\'s Encrypt' },
    { key: 'private_link',     label: 'Private Link / VPC Peering', hint: 'Isolate traffic from public internet' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((row, idx) => (
        <Toggle
          key={row.key}
          first={idx === 0}
          label={row.label}
          hint={row.hint}
          value={value[row.key]}
          onChange={(v) => onChange({ [row.key]: v })}
        />
      ))}
    </div>
  );
}

export function Toggle({ first, label, hint, value, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 0',
      borderTop: first ? 'none' : `1px solid ${T.stroke2}`,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{label}</div>
        <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>{hint}</div>
      </div>
      <Switch value={!!value} onChange={onChange} />
    </div>
  );
}

export function Switch({ value, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      style={{
        width: 40, height: 22, borderRadius: 999,
        background: value ? T.action : T.stroke,
        border: 'none', cursor: 'pointer',
        position: 'relative', transition: 'background .15s',
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: value ? 20 : 2,
        width: 18, height: 18, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.18)',
        transition: 'left .15s',
      }} />
    </button>
  );
}

function SelfHostedCreds({ cloud, provider, update }) {
  // Only GCP is wired live today; AWS/Azure show as coming soon. Self-Hosted
  // needs the customer's WIF audience + deploy SA to mint short-lived OIDC
  // tokens at runtime (see GcpAccessTokenMinter + StubPlatformIdentitySource).
  if (provider !== 'gcp') {
    return (
      <div style={{
        marginTop: 16, padding: 12,
        background: T.warnBg, color: T.warn,
        border: `1px solid ${T.warn}33`, borderRadius: 4,
        fontSize: 12,
      }}>
        Self-Hosted for <strong>{provider.toUpperCase()}</strong> is not yet wired.
        GCP first — AWS + Azure land in the W4 follow-on.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>
        GCP Account Credentials
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <Field label="Account label" hint="Friendly name shown in the catalog.">
          <input
            style={inp}
            placeholder="Acme prod (us-central1)"
            value={cloud.name}
            onChange={(e) => update({ name: e.target.value })}
          />
        </Field>
        <Field label="GCP Project ID" required>
          <input
            style={inp}
            placeholder="acme-prove7-prod"
            value={cloud.gcp_project_id}
            onChange={(e) => update({ gcp_project_id: e.target.value })}
          />
        </Field>
        <Field label="Workload Identity Pool" required>
          <input
            style={inp}
            placeholder="prove7-pool"
            value={cloud.wif_pool_id}
            onChange={(e) => update({ wif_pool_id: e.target.value })}
          />
        </Field>
        <Field label="WIF Provider" required>
          <input
            style={inp}
            placeholder="prove7-github"
            value={cloud.wif_provider_id}
            onChange={(e) => update({ wif_provider_id: e.target.value })}
          />
        </Field>
        <Field label="Deploy Service Account email" required
          hint="The SA Prove7 impersonates after the WIF exchange.">
          <input
            style={inp}
            placeholder="prove7-deploy@acme-prove7-prod.iam.gserviceaccount.com"
            value={cloud.deploy_service_account_email}
            onChange={(e) => update({ deploy_service_account_email: e.target.value })}
          />
        </Field>
      </div>
    </div>
  );
}

// Map the self-hosted credential form (snake_case UI state) onto the camelCase
// CloudAccountBlock the backend binds. Keeps the UI state shape untouched.
function toCloudAccountBlock(c) {
  if (!c) return null;
  return {
    name: c.name || null,
    gcpProjectId: c.gcp_project_id || null,
    wifPoolId: c.wif_pool_id || null,
    wifProviderId: c.wif_provider_id || null,
    deployServiceAccountEmail: c.deploy_service_account_email || null,
  };
}

function validate(f, env) {
  const errs = [];
  // Contract-driven: a customer and an entitled deployment line authorize the deploy.
  if (!f.customer_id)             errs.push('Customer');
  if (!f.order_line_id)           errs.push('Entitled deployment');
  if (!f.deployment_name?.trim()) errs.push('Deployment Name');
  if (!f.platform_version)        errs.push('Platform Version');
  // Self-hosted credentials are required only when the order form contracts a
  // self-hosted GCP deployment (the only self-hosted target wired today).
  if (env && env.deploymentMode === 'self_hosted' && env.provider === 'gcp') {
    if (!f.cloud_account?.gcp_project_id)               errs.push('GCP Project ID');
    if (!f.cloud_account?.wif_pool_id)                  errs.push('WIF Pool');
    if (!f.cloud_account?.wif_provider_id)              errs.push('WIF Provider');
    if (!f.cloud_account?.deploy_service_account_email) errs.push('Deploy SA email');
  }
  return errs;
}

function buildPayload(f, env) {
  // Contract-driven deploy. The backend fulfils the entitled order-form line and
  // takes ALL contracted infra (mode, provider, region, size, SLA) FROM the line —
  // we send only the identifiers plus the non-contracted operator inputs. Request
  // bodies bind to the camelCase QuickDeployRequest fields (Jackson default), so the
  // top-level keys are camelCase; the resilience/networking/observability bags are
  // stored verbatim in config_overrides and keep their own keys.
  if (env) {
    return {
      customerId: f.customer_id,
      orderLineId: f.order_line_id,
      deploymentName: f.deployment_name.trim(),
      platformVersion: f.platform_version,
      cloudAccount: env.deploymentMode === 'self_hosted' ? toCloudAccountBlock(f.cloud_account) : null,
      resilience: f.resilience,
      networking: f.networking,
      observability: f.observability,
    };
  }
  // Legacy free-form fallback (no entitled line). Not reachable from this UI, which
  // requires a customer + line, but kept so a direct caller still binds correctly.
  return {
    mode: f.mode,
    deployment_name: f.deployment_name.trim(),
    provider: f.provider,
    primary_region: f.primary_region,
    paired_region: f.paired_region || null,
    sla_tier: f.sla_tier,
    instance_size: f.instance_size,
    platform_version: f.platform_version,
    cloud_account: f.mode === 'self_hosted' ? f.cloud_account : null,
    resilience: f.resilience,
    networking: f.networking,
    observability: f.observability,
  };
}

/**
 * Honest pre-deploy readiness. The GCP/helm connector skills default to
 * dry-run (prove7.gcp.live-execution.enabled=false): they return synthetic
 * outputs and create NO cloud resources, yet the run still reports success.
 * The operator must know that BEFORE clicking Deploy, not afterwards.
 */
function PreflightBanner({ preflight }) {
  if (!preflight) return null;
  const dryRun = preflight.execution_mode !== 'live';
  const failed = (preflight.checks || []).filter((c) => !c.ok);
  const tone = dryRun ? { fg: T.warn, bg: T.warnBg } : { fg: T.success, bg: T.successBg };

  return (
    <div style={{
      marginTop: 12, padding: '10px 12px',
      background: tone.bg, color: tone.fg,
      border: `1px solid ${tone.fg}33`, borderRadius: 4, fontSize: 12,
    }}>
      <strong>
        {dryRun ? 'Dry run — nothing will be provisioned' : 'Live — real cloud resources will be created'}
      </strong>
      {dryRun && (
        <div style={{ marginTop: 4 }}>
          This deploy will run end-to-end and report success, but every step returns
          simulated output. No VPC, database or cluster is created.
        </div>
      )}
      {failed.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {failed.map((c) => (
            <li key={c.id}><strong>{c.label}:</strong> {c.detail}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
